import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import InlineTable, { InlineColDef } from '../util/InlineTable';

// ── Emoji picker (same as original) ──────────────────────────────────────────

const EMOJI_OPTIONS = [
  '🏢','🏠','🤒','🌴','🚫','🧑‍💻','📍','✈️','🚗','🏥',
  '☕','🎯','📚','💼','🔧','🎉','⏰','🌿','🏋️','🎵',
  '🌞','🌙','🔴','🟢','🟡','⚡','💬','📞','🖥️','🗓️',
];

const EmojiPicker: React.FC<{ value: string; onChange: (e: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen(v => !v)}
        style={{ width: 34, height: 28, fontSize: 18, borderRadius: 6, border: 'var(--border-input)', background: 'var(--bg-input)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {value || '?'}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 34, left: 0, zIndex: 200, background: 'var(--bg-form)', border: 'var(--border-input)', borderRadius: 8, padding: 8, display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, boxShadow: '0 4px 16px rgba(0,0,0,.15)', width: 220 }}>
          {EMOJI_OPTIONS.map(e => (
            <button key={e} type="button" onClick={() => { onChange(e); setOpen(false); }}
              style={{ fontSize: 18, padding: 4, borderRadius: 6, border: 'none', cursor: 'pointer', background: value === e ? 'var(--btn-user-hover)' : 'transparent' }}>
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface Status {
  id: number;
  name: string;
  color: string;
  emoji: string;
  isSystem: boolean;
}

// ── Column definitions ────────────────────────────────────────────────────────

const columns: InlineColDef<Status>[] = [
  {
    key: 'emoji',
    label: 'Emoji',
    type: 'readonly',
    width: 50,
    render: (row) => <span style={{ fontSize: 20 }}>{row.emoji}</span>,
  },
  {
    key: 'color',
    label: 'Color',
    type: 'color',
    width: 60,
  },
  {
    key: 'name',
    label: t('WorkingStatusName') || 'Name',
    type: 'text',
    placeholder: t('WorkingStatusName') || 'Status name...',
  },
  {
    key: 'isSystem',
    label: '',
    type: 'readonly',
    width: 70,
    editable: false,
    render: (row) => row.isSystem
      ? <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', background: 'var(--bg-input)', padding: '2px 6px', borderRadius: 3, border: 'var(--border-input)' }}>system</span>
      : null,
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const HrWorkingStatusSettingsNew: React.FC = () => {
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('hr-working-status');
      const list = Array.isArray(data) ? data : [];
      setStatuses(list);
      if (list.length === 0) {
        await axios.post('hr-working-status/seed');
        const { data: seeded } = await axios.get('hr-working-status');
        setStatuses(Array.isArray(seeded) ? seeded : []);
      }
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (row: Partial<Status>) => {
    if (!row.name?.trim()) return;
    await axios.post('hr-working-status/add', {
      name: row.name.trim(),
      color: row.color || '#4f8ef7',
      emoji: row.emoji || '💼',
    });
    await load();
  };

  const handleEdit = async (id: string | number, row: Partial<Status>) => {
    await axios.put(`hr-working-status/${id}`, {
      name: row.name,
      color: row.color,
      emoji: row.emoji,
    });
    await load();
  };

  const handleDelete = async (id: string | number) => {
    await axios.delete(`hr-working-status/${id}`);
    await load();
  };

  return (
    <div className="bg-[var(--bg-form)] rounded-[5px]" style={{ border: 'var(--border-input)', overflow: 'hidden' }}>
      <InlineTable<Status>
        columns={columns}
        rows={statuses}
        getKey={(s) => s.id}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canDelete={(s) => !s.isSystem}
        addLabel={`+ ${t('AddWorkingStatus') || 'Add Status'}`}
        emptyText={t('WorkingStatusSetup') || 'No statuses yet'}
        defaultValues={{ emoji: '💼', color: '#4f8ef7' }}
        // EmojiPicker is complex — override edit/add form to include it
        renderExpandedForm={(form, onChange, onSave, onCancel) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flex: 1, alignItems: 'center', gap: 8, minWidth: 0 }}>
              <EmojiPicker value={form.emoji || '💼'} onChange={v => onChange('emoji', v)} />
              <input
                type="text"
                value={form.name || ''}
                onChange={e => onChange('name', e.target.value)}
                placeholder={t('WorkingStatusName') || 'Status name...'}
                autoFocus
                style={{ flex: 1, height: 28, border: 'var(--border-input)', borderRadius: 'var(--radius-input, 5px)', background: 'var(--bg-input)', color: 'var(--text-color)', fontSize: 12, padding: '0 8px', fontFamily: 'inherit', outline: 'none' }}
              />
              <input
                type="color"
                value={form.color || '#4f8ef7'}
                onChange={e => onChange('color', e.target.value)}
                title="Color"
                style={{ width: 32, height: 28, border: 'var(--border-input)', borderRadius: 6, padding: 2, cursor: 'pointer' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" className="btn-icon" onClick={onSave}
                style={{ color: 'var(--color-success, #059669)', fontSize: 14 }} title="Save">✓</button>
              <button type="button" className="btn-icon" onClick={onCancel}
                style={{ color: 'var(--text-color-calendar-week)', fontSize: 14 }} title="Cancel">✕</button>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default HrWorkingStatusSettingsNew;
